import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setStaffInfo } from "redux/actions";
import Card from "components/Card";
import CardHeader from "components/CardHeader";
import FormContainer from "components/FormContainer";
import PhoneNumberInput from "components/PhoneNumberInput";
import Input from "components/Input";
import Dropdown from "components/Dropdown";
import Checkbox from "components/Checkbox";
import { useQuery } from "@apollo/client";
import { GET_ALL_STAFF_ROLES } from "graphql/service";
import { usePlacesWidget } from "react-google-autocomplete";
import { InputRow } from "../../styles";
import { debouncedErrorMessage } from "utils/messageUtil";

const StaffInformation = ({ staffInfoError, setStaffInfoError, btnInfo }) => {
  const dispatch = useDispatch();
  const staffInfo = useSelector((state) => state.staffInfoReducer.staffInfo);
  const phoneNumber = staffInfo.phoneNumber || "";
  const [phoneNumberValidity, setPhoneNumberValidity] = useState(false);

  const updatingRef = useRef(false);

  const handlePhoneChange = (number) => {
    if (updatingRef.current) return;
    if (number === phoneNumber) return;
    // ignore library reset during init
    if (number === "" && phoneNumber !== "") return;
    updatingRef.current = true;
    dispatch(setStaffInfo({phoneNumber: number }));
    setStaffInfoError((prev) => ({
      ...prev,
      phoneNumberError: false,
    }));
    updatingRef.current = false;
  };

  const handleValidityChange = (valid) => {
    setPhoneNumberValidity(valid);
  };

  const { ref } = usePlacesWidget({
    apiKey: `${process.env.REACT_APP_GOOGLE_APIKEY}&loading=async`,
    onPlaceSelected: (place) => {
      const components = place.address_components || [];

      const getComponent = (type) =>
        components.find((component) => component.types.includes(type))
          ?.long_name || "";

      const streetNumber = getComponent("street_number");
      const route = getComponent("route");

      const streetAddress = [streetNumber, route].filter(Boolean).join(" ");
      const streetAddressLine2 = ""; // Google Autocomplete doesn't return this
      const city =
        getComponent("locality") || getComponent("sublocality") || "";
      const province = getComponent("administrative_area_level_1");
      const postalCode = getComponent("postal_code");
      const country = getComponent("country");
      dispatch(
        setStaffInfo({
          streetAddress,
          streetAddressLine2,
          city,
          province,
          postalCode,
          country,
        })
      );
    },
    options: {
      types: ["address"],
    },
  });

  const { data, error } = useQuery(GET_ALL_STAFF_ROLES);

  const roleOptions = useMemo(() => {
    if (!data?.validStaffRoles?.length) return [];
    return data.validStaffRoles.map((role) => ({
      label: role.name,
      value: role.roleId,
    }));
  }, [data]);

  useEffect(() => {
    if (!error) return;

    if (!error.networkError) {
      let content = error.message;

      if (error.message.includes("Validation")) {
        content = JSON.stringify(error.graphQLErrors[0]?.extensions?.exception);
      }

      debouncedErrorMessage(content);
    }
  }, [error]);

  return (
    <Card>
      <CardHeader
        additionalstyle={`
          @media only screen and (max-width: 767px) {
            .buttonContainer {
              width: 100%;
              justify-content: space-between;
            }
            
            flex-direction: column;
            justify-content: center;
          }
        `}
      >
        <div className="cardTitle">STAFF INFORMATION</div>
      </CardHeader>
      <FormContainer>
        <InputRow>
          <Input
            mainPage
            title="NAME"
            value={staffInfo.fullName || ""}
            error={staffInfoError.fullNameError}
            onChange={(e) => {
              dispatch(setStaffInfo({ fullName: e.target.value }));
            }}
            additionalstyle={`
            width: 100%;
          `}
          />
          <Dropdown
            mainPage
            options={roleOptions}
            value={
              roleOptions.find((option) => option.value === staffInfo.staffRoleIds[0])
                ?.label || ""
            }
            error={staffInfoError.rolesError}
            title="ROLE"
            onChange={(e) => {
              dispatch(setStaffInfo({ staffRoleIds: [e] }));
            }}
            additionalstyle={`
            width: 100%;
          `}
          />
        </InputRow>
        <InputRow>
          <div className="address">
            <Input
              mainPage
              title="ADDRESS"
              inputFieldRef={ref}
              value={staffInfo.streetAddress || ""}
              error={staffInfoError.streetAddressError}
              onChange={(e) => {
                dispatch(setStaffInfo({ streetAddress: e.target.value }));
              }}
              additionalstyle={`
            width: 100%;
          `}
            />
          </div>
          <div className="address">
            <Input
              mainPage
              title="ADDRESS LINE 2"
              value={staffInfo.streetAddressLine2 || ""}
              error={staffInfoError.streetAddressLine2Error}
              onChange={(e) => {
                dispatch(setStaffInfo({ streetAddressLine2: e.target.value }));
              }}
              additionalstyle={`
            width: 100%;
          `}
            />
          </div>
        </InputRow>
        <InputRow>
          <div className="city">
            <Input
              mainPage
              title="CITY"
              value={staffInfo.city || ""}
              error={staffInfoError.cityError}
              onChange={(e) => {
                dispatch(setStaffInfo({ city: e.target.value }));
              }}
              additionalstyle={`
            width: 100%;
          `}
            />
          </div>
          <div className="province">
            <Input
              mainPage
              title="PROVINCE / STATE"
              value={staffInfo.province || ""}
              error={staffInfoError.provinceError}
              onChange={(e) => {
                dispatch(setStaffInfo({ province: e.target.value }));
              }}
              additionalstyle={`
            width: 100%;
          `}
            />
          </div>
          <div className="zipCode">
            <Input
              mainPage
              title="POSTAL / ZIP CODE"
              value={staffInfo.postalCode || ""}
              error={staffInfoError.postalCodeError}
              onChange={(e) => {
                dispatch(setStaffInfo({ postalCode: e.target.value }));
              }}
              additionalstyle={`
            width: 100%;
          `}
            />
          </div>
          <div className="country">
            <Input
              mainPage
              title="COUNTRY"
              value={staffInfo.country || ""}
              error={staffInfoError.countryError}
              onChange={(e) => {
                dispatch(setStaffInfo({ country: e.target.value }));
              }}
              additionalstyle={`
            width: 100%;
          `}
            />
          </div>
        </InputRow>
        <InputRow>
          <div className="email">
            <Input
              mainPage
              title="EMAIL"
              value={staffInfo.email || ""}
              error={staffInfoError.emailError}
              onChange={(e) => {
                dispatch(setStaffInfo({ email: e.target.value }));
              }}
              additionalstyle={`
            width: 100%;
          `}
            />
          </div>
          <div className="phone">
            <PhoneNumberInput
              mainPage
              title="PHONE"
              error={staffInfoError.phoneNumberError}
              value={phoneNumber}
              onChangeNumber={handlePhoneChange}
              onPhoneValidityChange={handleValidityChange}
              onBlur={() => {
                setStaffInfoError((prev) => ({
                  ...prev,
                  phoneNumberError:
                    !phoneNumberValidity && phoneNumber.length > 0,
                }));
              }}
              additionalstyle={`
            width: 100%;
          `}
            />
          </div>
        </InputRow>
        <InputRow>
          <Input
            mainPage
            title="6-DIGIT PIN"
            maxLength={6}
            value={staffInfo.PIN}
            error={staffInfoError.PINError}
            errorMessage={"PIN must be 6 digits"}
            placeholder="******"
            onChange={(e) => {
              const result = e.target.value.replace(/\D/g, "");
              dispatch(setStaffInfo({ PIN: result }));
              setStaffInfoError({ ...staffInfoError, PINError: false });
            }}
            additionalstyle={`
            width: 150px;
          `}
          />
        </InputRow>
        <Checkbox
          checked={!staffInfo.isActive}
          onChange={() =>
            dispatch(setStaffInfo({ isActive: !staffInfo.isActive }))
          }
          title="Deactivate Account"
          additionalstyle={`
            margin: 24px 0 0 2px;
          `}
        />
      </FormContainer>
      <div className="buttonContainer">{btnInfo}</div>
    </Card>
  );
};

export default StaffInformation;
